import { Box, Button, Heading, Spinner, Text, VStack } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import serverGenerationHubService from "services/serverGenerationHubService";
import { getThemeColor } from "theme/colors";

interface Step4ErrorHandlingProps {
  serverId: string;
  errorMessage?: string;
  onRetry: () => void;
  onStartOver: () => void;
}

export const Step4ErrorHandling = ({
  serverId,
  errorMessage,
  onRetry,
  onStartOver,
}: Step4ErrorHandlingProps) => {
  const { t } = useTranslation();
  const [isReconnecting, setIsReconnecting] = useState(true);
  const [connectionAttempts, setConnectionAttempts] = useState(0);

  useEffect(() => {
    let reconnectInterval: NodeJS.Timeout;

    const attemptReconnection = async () => {
      try {
        setIsReconnecting(true);
        await serverGenerationHubService.start();

        // If we successfully reconnect, go back to progress view
        onRetry();
      } catch (error) {
        setConnectionAttempts((prev) => prev + 1);

        // After 5 attempts, stop trying automatically
        if (connectionAttempts >= 5) {
          setIsReconnecting(false);
          clearInterval(reconnectInterval);
        }
      }
    };

    // Try to reconnect every 5 seconds
    reconnectInterval = setInterval(attemptReconnection, 5000);

    // Initial attempt
    attemptReconnection();

    return () => {
      if (reconnectInterval) {
        clearInterval(reconnectInterval);
      }
    };
  }, [connectionAttempts, onRetry, serverId]);

  return (
    <Box position="relative" h="full" display="flex" flexDirection="column">
      {/* Scrollable Content */}
      <Box flex="1" overflowY="auto" px={6} py={4}>
        <VStack gap={6} align="stretch">
          <Box textAlign="center">
            <Box fontSize="5xl" mb={3}>
              {isReconnecting ? "🔄" : "⚠️"}
            </Box>
            <Heading
              size="lg"
              mb={2}
              color={isReconnecting ? "blue.600" : "red.600"}
            >
              {isReconnecting
                ? t("app.onboarding.step4.connectionInterrupted")
                : t("app.onboarding.step4.generationError")}
            </Heading>
            <Text color={getThemeColor("text.secondary")} fontSize="sm">
              {isReconnecting
                ? t("app.onboarding.step4.reconnecting")
                : t("app.onboarding.step4.encounterIssue")}
            </Text>
          </Box>

          {/* Error Message */}
          {errorMessage && (
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={4}
              bg={getThemeColor("alert.error.bg")}
              borderColor={getThemeColor("alert.error.border")}
            >
              <Heading
                size="xs"
                mb={2}
                color={getThemeColor("alert.error.text")}
              >
                {t("app.onboarding.step4.errorDetails")}
              </Heading>
              <Text
                color={getThemeColor("alert.error.text")}
                fontSize="xs"
                fontFamily="mono"
              >
                {errorMessage}
              </Text>
            </Box>
          )}

          {/* Reconnection Status */}
          {isReconnecting && (
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={4}
              bg={getThemeColor("alert.info.bg")}
              borderColor={getThemeColor("alert.info.border")}
              textAlign="center"
            >
              <Spinner size="md" color="blue.500" mb={3} />
              <Text
                color={getThemeColor("alert.info.text")}
                fontWeight="semibold"
                mb={1}
                fontSize="sm"
              >
                {t("app.onboarding.step4.attemptingReconnect")}
              </Text>
              <Text fontSize="xs" color={getThemeColor("alert.info.text")}>
                {t("app.onboarding.step4.attemptCount", {
                  current: connectionAttempts + 1,
                  max: 5,
                })}
              </Text>
              <Text
                fontSize="xs"
                color={getThemeColor("text.secondary")}
                mt={3}
              >
                {t("app.onboarding.step4.backgroundProcess")}
              </Text>
            </Box>
          )}

          {/* Manual Retry Failed */}
          {!isReconnecting && (
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={4}
              bg={getThemeColor("alert.warning.bg")}
              borderColor={getThemeColor("alert.warning.border")}
            >
              <Heading
                size="xs"
                mb={2}
                color={getThemeColor("alert.warning.text")}
              >
                {t("app.onboarding.step4.unableReconnect")}
              </Heading>
              <Text
                fontSize="xs"
                color={getThemeColor("alert.warning.text")}
                mb={3}
              >
                {t("app.onboarding.step4.reconnectExhausted")}
              </Text>
              <VStack
                align="start"
                gap={1}
                pl={3}
                fontSize="xs"
                color={getThemeColor("text.primary")}
              >
                <Text>• {t("app.onboarding.step4.networkIssues")}</Text>
                <Text>• {t("app.onboarding.step4.serverMaintenance")}</Text>
                <Text>• {t("app.onboarding.step4.invalidSpec")}</Text>
                <Text>• {t("app.onboarding.step4.rateLimiting")}</Text>
              </VStack>
            </Box>
          )}

          {/* Help Text */}
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={3}
            bg={getThemeColor("bg.secondary")}
            borderColor={getThemeColor("border.default")}
          >
            <Text fontSize="xs" color={getThemeColor("text.primary")}>
              <strong>{t("app.onboarding.step4.needHelp")}</strong>{" "}
              {t("app.onboarding.step4.ifIssuePersists")}
            </Text>
            <VStack
              align="start"
              gap={1}
              pl={3}
              mt={2}
              fontSize="xs"
              color={getThemeColor("text.secondary")}
            >
              <Text>• {t("app.onboarding.step4.checkApiUrl")}</Text>
              <Text>• {t("app.onboarding.step4.checkApiFormat")}</Text>
              <Text>• {t("app.onboarding.step4.checkConnection")}</Text>
              <Text>• The API server is not rate-limiting requests</Text>
            </VStack>
          </Box>
        </VStack>
      </Box>

      {/* Sticky Footer */}
      <Box
        position="sticky"
        bottom={0}
        borderTopWidth="1px"
        borderColor={getThemeColor("border.default")}
        bg={getThemeColor("modal.bg")}
        py={4}
        px={6}
        boxShadow="sm"
      >
        <VStack gap={2}>
          <Button
            colorPalette="blue"
            onClick={onRetry}
            size="md"
            width="full"
            disabled={isReconnecting}
          >
            {isReconnecting ? "Reconnecting..." : "Try Reconnecting Now"}
          </Button>

          <Button
            variant="outline"
            colorPalette="gray"
            onClick={onStartOver}
            size="md"
            width="full"
          >
            {t("app.onboarding.step4.startOver")}
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};
